import { useEffect, useState, useRef } from "react";
import { Megaphone, Send, Loader2, FileText, Search, Users, CheckCircle2, Clock, Printer, Eye, Bell, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ROLES, can } from "../lib/constants";
import {
  circularsListEmployees, circularsCreate, circularsIssue, circularsOutgoing, circularsIncoming,
  circularsGet, circularsSign, circularsTracking, circularsRemind, circularsArchive, circularsPdfUrl, circularsDelete,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const TEACHER_ROLES = ["teacher"];
const ADMIN_ROLES = ["admin", "supervisor", "student_affairs", "student_guide"];

export default function Circulars() {
  const { user } = useAuth();
  const canIssue = can.issueCirculars(user);
  const canViewSign = can.viewSignCirculars(user) || canIssue;

  const ALL_TABS = [
    { key: "create", label: "إصدار تعميم", icon: Send, show: () => canIssue },
    { key: "outgoing", label: "التعاميم الصادرة", icon: FileText, show: () => canIssue },
    { key: "incoming", label: "التعاميم الواردة", icon: Bell, show: () => canViewSign },
    { key: "archive", label: "الأرشيف والبحث", icon: Search, show: () => canViewSign },
  ];
  const TABS = ALL_TABS.filter((t) => t.show());
  const [tab, setTab] = useState(TABS[0]?.key || "incoming");
  const [openCircularId, setOpenCircularId] = useState(null);

  if (!canViewSign) {
    return (
      <div>
        <PageHeader title="التعاميم الداخلية" subtitle="ليس لديك صلاحية الوصول لهذا القسم" icon={Megaphone} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="التعاميم الداخلية" subtitle="إصدار واطّلاع وتوقيع إلكتروني على التعاميم الرسمية" icon={Megaphone} />

      <div className="flex gap-2 mb-5 border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setOpenCircularId(null); }}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-cairo font-bold border-b-2 whitespace-nowrap transition-colors ${
              tab === t.key ? "border-sky-700 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {openCircularId ? (
        <CircularView cid={openCircularId} onBack={() => setOpenCircularId(null)} canIssue={canIssue} />
      ) : (
        <>
          {tab === "create" && <CreateCircularTab onIssued={(cid) => { setTab("outgoing"); setOpenCircularId(null); }} />}
          {tab === "outgoing" && <OutgoingTab onOpen={setOpenCircularId} />}
          {tab === "incoming" && <IncomingTab onOpen={setOpenCircularId} />}
          {tab === "archive" && <ArchiveTab onOpen={setOpenCircularId} />}
        </>
      )}
    </div>
  );
}

// ============ إصدار تعميم جديد ============
function CreateCircularTab({ onIssued }) {
  const [title, setTitle] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(null); // التعميم بعد الإنشاء كمسودة، قبل الإصدار
  const [previewHtml, setPreviewHtml] = useState(null);
  const [issuing, setIssuing] = useState(false);

  useEffect(() => {
    circularsListEmployees().then(setEmployees).catch(() => toast.error("تعذر تحميل الموظفين"));
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectByRoles = (roles) => {
    setSelected((prev) => {
      const next = new Set(prev);
      employees.filter((e) => roles.includes(e.role)).forEach((e) => next.add(e.id));
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(employees.map((e) => e.id)));
  const clearAll = () => setSelected(new Set());

  const filtered = employees.filter((e) => !search.trim() || e.full_name.toLowerCase().includes(search.trim().toLowerCase()));

  const doCreate = async () => {
    if (!title.trim()) { toast.error("عنوان التعميم مطلوب"); return; }
    if (!bodyHtml.trim()) { toast.error("موضوع التعميم مطلوب"); return; }
    if (selected.size === 0) { toast.error("حدد مستهدفًا واحدًا على الأقل"); return; }
    setCreating(true);
    try {
      const res = await circularsCreate({ title: title.trim(), body_html: bodyHtml, target_user_ids: Array.from(selected) });
      setDraft(res);
      toast.success("تم إنشاء مسودة التعميم. راجعها قبل الإصدار النهائي.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الإنشاء");
    } finally {
      setCreating(false);
    }
  };

  const doPreview = async () => {
    if (!draft) return;
    try {
      const { circularsPreview } = await import("../lib/api");
      const html = await circularsPreview(draft.id);
      setPreviewHtml(html);
    } catch {
      toast.error("تعذر تحميل المعاينة");
    }
  };

  const doIssue = async () => {
    if (!draft) return;
    setIssuing(true);
    try {
      await circularsIssue(draft.id);
      toast.success(`تم إصدار التعميم رقم بنجاح إلى ${draft.target_count} موظف`);
      onIssued(draft.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الإصدار");
    } finally {
      setIssuing(false);
    }
  };

  if (draft) {
    return (
      <div className="space-y-4">
        <Card className="p-5 border-sky-200 bg-sky-50/30">
          <div className="flex items-center gap-2 font-cairo font-bold text-sky-800 mb-1">
            <CheckCircle2 size={18} /> تم إنشاء المسودة
          </div>
          <div className="text-sm text-slate-700">
            العنوان: <b>{draft.title}</b> — عدد المستهدفين: <b>{draft.target_count}</b>
          </div>
          <div className="flex gap-2 mt-4 flex-wrap">
            <Button onClick={doPreview} variant="outline" className="h-10 gap-2"><Eye size={16} /> معاينة التعميم</Button>
            <Button onClick={doIssue} disabled={issuing} className="h-10 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
              {issuing ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} تأكيد وإصدار إلى {draft.target_count} موظف
            </Button>
            <Button onClick={() => { setDraft(null); setPreviewHtml(null); }} variant="outline" className="h-10">إلغاء والعودة للتعديل</Button>
          </div>
        </Card>
        {previewHtml && (
          <Card className="p-0 overflow-hidden border-slate-300">
            <iframe title="معاينة التعميم" srcDoc={previewHtml} className="w-full" style={{ height: "900px", border: "none" }} />
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Card className="p-5 border-slate-200">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>عنوان التعميم</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="مثال: بشأن الالتزام بمواعيد الحضور والانصراف" className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label>موضوع التعميم</Label>
            <textarea
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              placeholder="اكتب نص التعميم هنا... (يمكنك استخدام فقرات، وأرقام مثل 1- 2- 3-)"
              className="w-full min-h-[180px] rounded-lg border border-slate-300 p-3 text-sm font-cairo leading-relaxed focus:outline-none focus:ring-2 focus:ring-sky-200"
              dir="rtl"
            />
            <div className="text-[11px] text-slate-400">يمكنك فصل الفقرات بسطر فارغ، وستظهر منسقة تلقائيًا في القالب الرسمي.</div>
          </div>
        </div>
      </Card>

      <Card className="p-5 border-slate-200">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="font-cairo font-bold text-slate-800 flex items-center gap-1.5"><Users size={18} /> تحديد المستهدفين</div>
          <div className="text-sm font-cairo font-bold text-sky-700">عدد المستهدفين: {selected.size} موظف</div>
        </div>
        <div className="flex gap-2 flex-wrap mb-3">
          <Button onClick={() => selectByRoles(TEACHER_ROLES)} variant="outline" className="h-9 text-xs">تحديد جميع المعلمين</Button>
          <Button onClick={() => selectByRoles(ADMIN_ROLES)} variant="outline" className="h-9 text-xs">تحديد الإداريين</Button>
          <Button onClick={selectAll} variant="outline" className="h-9 text-xs">تحديد الجميع</Button>
          <Button onClick={clearAll} variant="outline" className="h-9 text-xs text-rose-600 border-rose-200">إلغاء التحديد</Button>
        </div>
        <div className="relative mb-3">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث باسم الموظف..." className="pr-9 h-10" />
        </div>
        <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg divide-y">
          {filtered.map((e) => (
            <label key={e.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} className="w-4 h-4 accent-sky-700" />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-800">{e.full_name}</div>
                <div className="text-[11px] text-slate-500">{e.role_label}</div>
              </div>
            </label>
          ))}
          {filtered.length === 0 && <div className="text-center text-sm text-slate-400 py-6">لا يوجد موظفون مطابقون</div>}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={doCreate} disabled={creating} className="h-11 px-6 bg-sky-700 hover:bg-sky-800 text-white gap-2">
          {creating ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} />} متابعة إلى المعاينة
        </Button>
      </div>
    </div>
  );
}

// ============ التعاميم الصادرة ============
function OutgoingTab({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      const data = await circularsOutgoing(search.trim() || undefined);
      setRows(data);
    } catch {
      toast.error("تعذر تحميل التعاميم الصادرة");
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async (c) => {
    if (!window.confirm(`هل أنت متأكد من حذف التعميم "${c.title}" نهائيًا؟ سيتم حذف جميع التوقيعات المرتبطة به، ولا يمكن التراجع عن هذا الإجراء.`)) return;
    setDeleting(c.id);
    try {
      await circularsDelete(c.id);
      toast.success("تم حذف التعميم نهائيًا");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحذف");
    } finally {
      setDeleting(null);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(load, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  if (loading) return <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-sky-700" /></div>;

  return (
    <div>
      <div className="relative mb-4">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث بعنوان التعميم..." className="pr-9 h-11" />
      </div>
      {rows.length === 0 ? (
        <div className="py-16 text-center text-slate-400">لا توجد تعاميم صادرة بعد.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((c) => (
            <Card key={c.id} className={`p-4 border-slate-200 ${c.status === "draft" ? "opacity-60" : ""}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-cairo font-bold text-slate-900">{c.title}</span>
                    {c.number && <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 tabular-nums">رقم {c.number}</span>}
                    {c.status === "draft" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">مسودة لم تُصدر</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    المستهدفون: {c.target_count} | اطّلع: {c.viewed_count ?? 0} | وقّع: {c.signed_count ?? 0}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {c.status === "issued" && (
                    <Button onClick={() => onOpen(c.id)} className="h-9 gap-1.5 bg-sky-700 hover:bg-sky-800 text-white text-xs">
                      <Eye size={14} /> فتح ومتابعة
                    </Button>
                  )}
                  <Button onClick={() => doDelete(c)} disabled={deleting === c.id} variant="outline" className="h-9 gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50 text-xs">
                    {deleting === c.id ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />} حذف
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ التعاميم الواردة ============
function IncomingTab({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const STATUS_LABELS = {
    pending: { text: "غير مقروء", color: "bg-rose-50 text-rose-700 border-rose-200" },
    viewed: { text: "تم الاطلاع فقط", color: "bg-amber-50 text-amber-700 border-amber-200" },
    signed: { text: "تم الاطلاع والتوقيع", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  };

  const load = async () => {
    try {
      const data = await circularsIncoming();
      setRows(data);
    } catch {
      toast.error("تعذر تحميل التعاميم الواردة");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-sky-700" /></div>;

  return (
    <div>
      {rows.length === 0 ? (
        <div className="py-16 text-center text-slate-400">لا توجد تعاميم واردة إليك حاليًا.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((c) => {
            const st = STATUS_LABELS[c.my_status] || STATUS_LABELS.pending;
            return (
              <Card key={c.id} className={`p-4 border-slate-200 ${c.my_status === "pending" ? "bg-rose-50/20 border-rose-200" : ""}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-cairo font-bold text-slate-900">{c.title}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${st.color}`}>{st.text}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">من: {c.sender_name} — رقم {c.number}</div>
                  </div>
                  <Button onClick={() => onOpen(c.id)} className="h-9 gap-1.5 bg-sky-700 hover:bg-sky-800 text-white text-xs shrink-0">
                    <Eye size={14} /> {c.my_status === "signed" ? "عرض" : "فتح والتوقيع"}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============ الأرشيف والبحث ============
function ArchiveTab({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const data = await circularsArchive({ q: search.trim() || undefined });
      setRows(data);
    } catch {
      toast.error("تعذر تحميل الأرشيف");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(load, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  if (loading) return <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-sky-700" /></div>;

  return (
    <div>
      <div className="relative mb-4">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث برقم أو عنوان أو موضوع التعميم..." className="pr-9 h-11" />
      </div>
      {rows.length === 0 ? (
        <div className="py-16 text-center text-slate-400">لا توجد نتائج.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((c) => (
            <Card key={c.id} className="p-4 border-slate-200">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-cairo font-bold text-slate-900">{c.title} <span className="text-[11px] text-slate-400 tabular-nums">#{c.number}</span></div>
                  <div className="text-xs text-slate-500 mt-1">
                    {c.sender_name} — {(c.issued_at || "").slice(0, 10)} — {c.signed_count}/{c.total_count} وقّعوا
                  </div>
                </div>
                <Button onClick={() => onOpen(c.id)} className="h-9 gap-1.5 bg-sky-700 hover:bg-sky-800 text-white text-xs shrink-0">
                  <Eye size={14} /> فتح
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ عرض تعميم واحد (قراءة/توقيع/متابعة/طباعة) ============
function CircularView({ cid, onBack }) {
  const [circular, setCircular] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(false);
  const [showUnsignedOnly, setShowUnsignedOnly] = useState(false);

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [signing, setSigning] = useState(false);

  const load = async () => {
    try {
      const c = await circularsGet(cid);
      setCircular(c);
      if (c.is_sender) {
        const t = await circularsTracking(cid, showUnsignedOnly);
        setTracking(t);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر تحميل التعميم");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [cid, showUnsignedOnly]);

  useEffect(() => {
    const interval = setInterval(() => {
      load();
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [cid, showUnsignedOnly]);

  const doRemind = async () => {
    setReminding(true);
    try {
      const res = await circularsRemind(cid);
      toast.success(`تم إرسال تذكير إلى ${res.notified} موظف`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر إرسال التذكير");
    } finally {
      setReminding(false);
    }
  };

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawingRef.current = true;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getPos(e, canvas);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0d3d47";
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const endDraw = () => { drawingRef.current = false; };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const cropSignature = (canvas) => {
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;
    const pixels = ctx.getImageData(0, 0, width, height).data;
    let minX = width, minY = height, maxX = 0, maxY = 0, found = false;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = pixels[(y * width + x) * 4 + 3];
        if (alpha > 10) {
          found = true;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (!found) return canvas.toDataURL("image/png");
    const pad = 4;
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(width, maxX + pad); maxY = Math.min(height, maxY + pad);
    const cropW = maxX - minX, cropH = maxY - minY;
    const cropped = document.createElement("canvas");
    cropped.width = cropW; cropped.height = cropH;
    cropped.getContext("2d").drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);
    return cropped.toDataURL("image/png");
  };

  const doSign = async () => {
    if (!acknowledged) { toast.error("يجب الإقرار بالاطلاع أولًا"); return; }
    if (!hasSignature) { toast.error("يرجى التوقيع في المربع المخصص"); return; }
    const canvas = canvasRef.current;
    const dataUrl = cropSignature(canvas);
    setSigning(true);
    try {
      await circularsSign(cid, dataUrl);
      toast.success("تم تسجيل اطلاعك وتوقيعك بنجاح");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التوقيع");
    } finally {
      setSigning(false);
    }
  };

  if (loading) return <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-sky-700" /></div>;
  if (!circular) return null;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button onClick={onBack} variant="outline" className="h-9">رجوع للقائمة</Button>
        {circular.is_sender && (
          <div className="flex gap-2">
            <Button onClick={doRemind} disabled={reminding} variant="outline" className="h-9 gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50 text-xs">
              {reminding ? <Loader2 className="animate-spin" size={14} /> : <Clock size={14} />} إرسال تذكير لغير الموقعين
            </Button>
            <a href={circularsPdfUrl(cid)} target="_blank" rel="noreferrer">
              <Button variant="outline" className="h-9 gap-1.5 text-xs"><Printer size={14} /> طباعة التعميم</Button>
            </a>
          </div>
        )}
      </div>

      {circular.is_sender && tracking && (
        <Card className="p-5 border-slate-200">
          <div className="font-cairo font-bold text-slate-800 mb-3">متابعة التعميم</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="text-center p-3 rounded-lg bg-slate-50">
              <div className="text-2xl font-extrabold text-slate-700 tabular-nums">{tracking.stats.total}</div>
              <div className="text-[11px] text-slate-500">المستهدفون</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-emerald-50">
              <div className="text-2xl font-extrabold text-emerald-700 tabular-nums">{tracking.stats.signed}</div>
              <div className="text-[11px] text-slate-500">تم التوقيع</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-amber-50">
              <div className="text-2xl font-extrabold text-amber-700 tabular-nums">{tracking.stats.viewed_only}</div>
              <div className="text-[11px] text-slate-500">اطّلع دون توقيع</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-rose-50">
              <div className="text-2xl font-extrabold text-rose-700 tabular-nums">{tracking.stats.pending}</div>
              <div className="text-[11px] text-slate-500">لم يطّلع</div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer w-fit">
            <input type="checkbox" checked={showUnsignedOnly} onChange={(e) => setShowUnsignedOnly(e.target.checked)} className="w-4 h-4 accent-sky-700" />
            عرض غير الموقعين فقط
          </label>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-right text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2">الموظف</th><th>المسمى</th><th>الحالة</th><th>التاريخ والوقت</th>
                </tr>
              </thead>
              <tbody>
                {tracking.targets.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100">
                    <td className="py-2 font-medium text-slate-800">{t.employee_name}</td>
                    <td className="text-slate-500">{t.employee_role_label}</td>
                    <td>
                      {t.status === "signed" && <span className="text-emerald-700 text-xs">✅ تم الاطلاع والتوقيع</span>}
                      {t.status === "viewed" && <span className="text-amber-700 text-xs">👁 اطّلع فقط</span>}
                      {t.status === "pending" && <span className="text-rose-700 text-xs">لم يطّلع</span>}
                    </td>
                    <td className="text-[11px] text-slate-400 tabular-nums">{(t.signed_at || t.viewed_at || "").replace("T", " ").slice(0, 16)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card className="p-5 sm:p-7 border-slate-200">
        <div className="text-xs text-slate-500 mb-1">تعميم رقم {circular.number} — {(circular.issued_at || "").slice(0, 10)}</div>
        <h2 className="font-cairo font-extrabold text-xl text-slate-900 mb-1">{circular.title}</h2>
        <div className="text-sm text-slate-500 mb-4">من: {circular.sender_role_label} {circular.sender_name}</div>
        <div className="prose prose-sm max-w-none font-cairo leading-loose whitespace-pre-wrap" dir="rtl">
          {circular.body_html}
        </div>
      </Card>

      {!circular.is_sender && circular.my_status !== "signed" && (
        <Card className="p-5 sm:p-7 border-sky-200 bg-sky-50/20">
          <label className="flex items-start gap-2.5 text-sm mb-4 cursor-pointer">
            <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} className="w-4 h-4 mt-0.5 accent-sky-700" />
            <span>أقر بالاطلاع على هذا التعميم وفهم مضمونه.</span>
          </label>
          <div className="text-sm font-cairo font-bold text-slate-700 mb-2">التوقيع الإلكتروني</div>
          <canvas
            ref={canvasRef}
            width={600} height={220}
            className="w-full border-2 border-dashed border-slate-300 rounded-lg bg-white touch-none"
            style={{ maxWidth: "600px" }}
            onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
            onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
          />
          <div className="flex gap-2 mt-3">
            <Button onClick={clearSignature} variant="outline" className="h-10 text-xs">مسح التوقيع</Button>
            <Button onClick={doSign} disabled={signing} className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
              {signing ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} تم الاطلاع والتوقيع
            </Button>
          </div>
        </Card>
      )}

      {!circular.is_sender && circular.my_status === "signed" && (
        <Card className="p-5 border-emerald-200 bg-emerald-50/30 text-center">
          <CheckCircle2 className="mx-auto text-emerald-600 mb-2" size={28} />
          <div className="font-cairo font-bold text-emerald-800">لقد قمت بالاطلاع والتوقيع على هذا التعميم بالفعل</div>
          {circular.my_signature && <img src={circular.my_signature} alt="توقيعك" className="mx-auto mt-3 h-16 object-contain" />}
        </Card>
      )}
    </div>
  );
}
